import { useState } from 'react';
import { useToast } from '../../hooks/use-toast';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { generateQuotePDFWithTemplate, TEMPLATES } from '../../utils/pdfTemplates';
import { downloadQuoteImage } from '../../utils/quoteImageGenerator';
import { generateInvoicePDF } from '../../utils/invoiceGenerator';
import { storeLead } from '../../utils/pdfGenerator';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

/**
 * Track user behavior and edge cases
 */
const trackUserBehavior = async (eventName, data) => {
  try {
    console.log('[USER_BEHAVIOR]', eventName, data);
    
    // Send to backend analytics
    await axios.post(`${API}/analytics/track`, {
      event: eventName,
      data,
      timestamp: new Date().toISOString()
    }, {
      withCredentials: true
    });
  } catch (error) {
    // Silent fail - don't break UX
    console.error('Analytics tracking failed:', error);
  }
};

/**
 * Custom hook for all quote actions
 * Handles PDF download, sending, invoice conversion
 */
export const useQuoteActions = ({
  quote,
  customerName,
  projectType,
  squareFeet,
  laborRate,
  markup,
  selectedTemplate,
  branding,
  projectPhotos,
  getProjectTypeData,
  quotaInfo,
  checkQuota
}) => {
  const [showLeadModal, setShowLeadModal] = useState(false);
  const [showSendModal, setShowSendModal] = useState(false);
  const [showPaywallModal, setShowPaywallModal] = useState(false);
  const [leadData, setLeadData] = useState(null);
  const [quoteId, setQuoteId] = useState(null);
  const [convertingToInvoice, setConvertingToInvoice] = useState(false);
  const { toast } = useToast();
  const { isAuthenticated, user } = useAuth();

  const checkPaywall = async () => {
    if (isAuthenticated && user?.subscription_status === 'free') {
      await checkQuota();
      if (quotaInfo && !quotaInfo.can_generate) {
        setShowPaywallModal(true);
        trackUserBehavior('paywall_hit', {
          action: 'quote_generation',
          quotesUsed: quotaInfo.quotesUsed,
          subscription: user?.subscription_status
        });
        return false;
      }
    }
    return true;
  };

  const handleDownloadPDF = async () => {
    const canProceed = await checkPaywall();
    if (!canProceed) return;
    
    trackUserBehavior('download_pdf_clicked', { template: selectedTemplate });
    setShowLeadModal(true);
  };

  const handleLeadSubmit = async (leadData) => {
    try {
      const templateConfig = TEMPLATES[selectedTemplate] || TEMPLATES.professional;
      
      // Track template usage
      trackUserBehavior('template_used', {
        template: selectedTemplate,
        tier: templateConfig.tier,
        userSubscription: user?.subscription_status
      });
      
      if (templateConfig?.tier === 'pro' && user?.subscription_status !== 'pro') {
        toast({
          title: "Pro Feature",
          description: "This template is only available with a Pro subscription. Upgrade to unlock all templates!",
          variant: "destructive",
          duration: 5000,
        });
        trackUserBehavior('pro_template_blocked', { template: selectedTemplate });
        return;
      }

      setLeadData(leadData);
      
      const leadResponse = await storeLead(leadData, {
        ...quote,
        customerName: customerName || leadData.name,
        projectType,
        squareFeet,
        laborRate,
        markup,
        template: selectedTemplate,
      });

      if (leadResponse && leadResponse.quote && leadResponse.quote.id) {
        setQuoteId(leadResponse.quote.id);
      }

      const filename = generateQuotePDFWithTemplate(
        {
          ...quote,
          customerName: customerName || leadData.name
        },
        leadData,
        {
          template: selectedTemplate,
          branding: branding,
          photos: projectPhotos
        }
      );

      // Increment quota for free users
      if (isAuthenticated && user?.subscription_status === 'free') {
        try {
          await axios.post(`${API}/auth/increment-quote-usage`, {}, {
            withCredentials: true
          });
          await checkQuota();
          trackUserBehavior('quota_incremented', {
            newCount: quotaInfo?.quotesUsed + 1
          });
        } catch (error) {
          console.error('Error incrementing quota:', error);
          trackUserBehavior('quota_increment_error', { error: error.message });
        }
      }

      setShowLeadModal(false);

      toast({
        title: "Quote Downloaded!",
        description: `${filename} has been saved using ${templateConfig?.name || 'Professional'} template.`,
        duration: 5000,
      });
      
      trackUserBehavior('pdf_downloaded_success', {
        template: selectedTemplate,
        quoteTotal: quote.total,
        projectType
      });
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast({
        title: "Error",
        description: "Failed to generate PDF. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      
      trackUserBehavior('pdf_generation_error', {
        error: error.message,
        template: selectedTemplate,
        quote
      });
    }
  };

  const handleSendQuote = async () => {
    const canProceed = await checkPaywall();
    if (!canProceed) return;
    
    trackUserBehavior('send_quote_clicked', {});
    setShowLeadModal(true);
  };

  const handleConvertToInvoice = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Login Required",
        description: "Please log in to convert quotes to invoices.",
        variant: "destructive",
      });
      trackUserBehavior('invoice_conversion_blocked', { reason: 'not_authenticated' });
      return;
    }

    const canProceed = await checkPaywall();
    if (!canProceed) return;

    if (!quoteId) {
      toast({
        title: "No Quote Found",
        description: "Please generate and save a quote first.",
        variant: "destructive",
      });
      trackUserBehavior('invoice_conversion_blocked', { reason: 'no_quote_id' });
      return;
    }

    setConvertingToInvoice(true);
    trackUserBehavior('invoice_conversion_started', { quoteId });
    
    try {
      const response = await axios.post(`${API}/invoices/from-quote/${quoteId}?tax_rate=0`, {}, {
        withCredentials: true
      });

      const invoiceData = response.data;
      const filename = generateInvoicePDF(invoiceData);

      toast({
        title: "Invoice Generated!",
        description: `Invoice ${invoiceData.invoice_number} created and downloaded. Send it to your client to get paid!`,
        duration: 6000,
      });
      
      trackUserBehavior('invoice_conversion_success', {
        invoiceNumber: invoiceData.invoice_number,
        total: invoiceData.total
      });
      
    } catch (error) {
      console.error('Error converting to invoice:', error);
      toast({
        title: "Error",
        description: error.response?.data?.detail || "Failed to create invoice. Please try again.",
        variant: "destructive",
        duration: 5000,
      });
      
      trackUserBehavior('invoice_conversion_error', {
        error: error.message,
        quoteId
      });
    } finally {
      setConvertingToInvoice(false);
    }
  };

  const handleDownloadQuoteImage = async () => {
    try {
      trackUserBehavior('quote_image_download_started', {});
      
      const filename = await downloadQuoteImage(
        quote,
        customerName || leadData?.name || 'Customer',
        branding
      );
      
      toast({
        title: "Quote Image Downloaded!",
        description: `${filename} is ready to share on social media. 🚀`,
        duration: 5000,
      });
      
      trackUserBehavior('quote_image_download_success', { filename });
      
    } catch (error) {
      console.error('Error downloading quote image:', error);
      toast({
        title: "Error",
        description: "Failed to generate quote image. Please try again.",
        variant: "destructive",
      });
      
      trackUserBehavior('quote_image_download_error', { error: error.message });
    }
  };

  return {
    // State
    showLeadModal,
    setShowLeadModal,
    showSendModal,
    setShowSendModal,
    showPaywallModal,
    setShowPaywallModal,
    leadData,
    setLeadData,
    quoteId,
    convertingToInvoice,
    
    // Actions
    handleDownloadPDF,
    handleLeadSubmit,
    handleSendQuote,
    handleConvertToInvoice,
    handleDownloadQuoteImage
  };
};
